import React from 'react';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import MessagePropType from '../definitions/MessagePropType';
import Colours from '../definitions/Colours';
import IconMapLazy from '../definitions/IconMapLazy';
import ACCORDION_VARIANTS from '../definitions/AccordionVariants';
import ACCORDION_MARKER_VARIANTS from '../definitions/AccordionMarkerVariants';
import ACCORDION_HEADER_SIZES from '../definitions/AccordionHeaderSizes';
import ACCORDION_HEADER_WEIGHTS from '../definitions/AccordionHeaderWeights';
import ACCORDION_BACKGROUND_COLOURS from '../definitions/AccordionBackgroundColours';
import AccordionBase from './AccordionBase';
import AccordionBasicItemHeader from './AccordionBasicItemHeader';
import AccordionBlueberryHeader from './AccordionBlueberryHeader';


function AccordionBasicItem({
    isExpanded,
    onClick,
    isInitiallyExpanded = false,

    title,
    titleColour = Colours.BLACKBERRY, // minimal only

    children,
    'data-name': dataName = 'Accordion',

    marker = ACCORDION_MARKER_VARIANTS.NONE,
    iconKey,
    number,

    size = ACCORDION_HEADER_SIZES.LARGE, // affects font size, padding & icon sizes.
    weight = ACCORDION_HEADER_WEIGHTS.REGULAR,

    // Customization options for content
    alwaysRenderChildMarkup = false,
    variant = ACCORDION_VARIANTS.DEFAULT,

    backgroundColour = ACCORDION_BACKGROUND_COLOURS.TRANSPARENT,

    ...otherProps
}) {
    const isDefaultVariant = variant === ACCORDION_VARIANTS.DEFAULT;

    const { HeaderComponent, accordionBodyClass } = getHeaderAndBodyCSS(variant);

    return (
        <AccordionBase
            isExpanded={isExpanded}
            isInitiallyExpanded={isInitiallyExpanded}
            onClick={onClick}
            HeaderComponent={HeaderComponent}
            headerProps={{
                title,
                'data-name': dataName,
                titleColour,

                marker: isDefaultVariant
                    ? marker
                    : undefined,
                iconKey: isDefaultVariant
                    ? iconKey
                    : undefined,
                number: isDefaultVariant
                    ? number
                    : undefined,

                // Generally speaking,
                // LARGE = wizards & landing pages
                // MEDIUM = landing pages
                // SMALL = minimal variant only
                size,
                weight,

                variant,
                backgroundColour,
            }}
            shouldIncludeDivider={isDefaultVariant}
            alwaysRenderChildMarkup={alwaysRenderChildMarkup}
            {...otherProps}
        >
            <div
                className={classNames(accordionBodyClass, {
                    'rh-my-0 rh-pb-1_5 rh-pl-0_75': isDefaultVariant,
                    'rh-pr-1_5': isDefaultVariant
                        && (marker === ACCORDION_MARKER_VARIANTS.DOT || marker === ACCORDION_MARKER_VARIANTS.ICON),
                })}
            >
                {children}
            </div>
        </AccordionBase>
    );
}

AccordionBasicItem.propTypes = {
    isExpanded: PropTypes.bool,
    onClick: PropTypes.func,
    isInitiallyExpanded: PropTypes.bool,

    title: MessagePropType.isRequired,

    children: PropTypes.node.isRequired,
    'data-name': PropTypes.string,

    titleColour: PropTypes.oneOf([ Colours.BLACKBERRY, Colours.BLUEBERRY_DARK ]),

    marker: PropTypes.oneOf(Object.values(ACCORDION_MARKER_VARIANTS)),
    iconKey: PropTypes.oneOf(Object.keys(IconMapLazy)),
    number: PropTypes.number,

    size: PropTypes.oneOf(Object.values(ACCORDION_HEADER_SIZES)),
    weight: PropTypes.oneOf(Object.values(ACCORDION_HEADER_WEIGHTS)),

    alwaysRenderChildMarkup: PropTypes.bool,

    variant: PropTypes.oneOf(Object.values(ACCORDION_VARIANTS)),

    // Because of the way we accept props here and pass them
    // to one of two header components which have different APIs,
    // we are forced to allow ALL background colours here.
    // So please only pass a backgroundColour which is supported
    // by the HeaderComponent you are using!
    backgroundColour: PropTypes.oneOf(Object.values(ACCORDION_BACKGROUND_COLOURS)),
};

const ACCORDION_SETTINGS = {
    [ACCORDION_VARIANTS.DEFAULT]: {
        HeaderComponent: AccordionBasicItemHeader,
        accordionBodyClass: undefined,
    },
    [ACCORDION_VARIANTS.MINIMAL]: {
        HeaderComponent: AccordionBasicItemHeader,
        accordionBodyClass: undefined,
    },
    [ACCORDION_VARIANTS.BLUEBERRY]: {
        HeaderComponent: AccordionBlueberryHeader,
        accordionBodyClass: 'rh-accordion-blueberry-body',
    },
};

function getHeaderAndBodyCSS(variant) {
    if (!ACCORDION_SETTINGS[variant]) {
        throw new RangeError(`AccordionBasicItem - unexpected variant: “${variant}”.
              It should be one of [ “${Object.values(ACCORDION_VARIANTS).join('”, “')}” ].`);
    }

    return ACCORDION_SETTINGS[variant];
}

export default observer(AccordionBasicItem);
